import { Flex, Input, Text } from '@chakra-ui/react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  formatPhoneNumberForBlocking,
  useCanBlockNumber,
} from '~/atoms/BlockedNumbersState'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { MangoModal } from '~/components/shared/MangoModal'
import { useBlockedNumberModalStyles } from '~/pages/Settings/componentStyles/BlockedNumberModal.styles'
import { Size } from '~/types/ThemeTypes'
import { formatPhoneNumber } from '~/utils/formatter'

interface BlockNumberModalProps {
  isOpen: boolean
  onClose: () => void
  onBlockNumber: (phoneNumber: string) => void
  isLoading: boolean
}

export const BlockNumberModal = ({
  isOpen,
  onClose,
  onBlockNumber,
  isLoading,
}: BlockNumberModalProps) => {
  const { t } = useTranslation()
  const [userInput, setUserInput] = useState('')

  const canBlockNumber = useCanBlockNumber(userInput)

  const styles = useBlockedNumberModalStyles()

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const lastChar = value.slice(-1)
    const isDeleting = userInput.length > value.length
    const isAddingSpecialChar = lastChar === ')' || lastChar === '-'

    // added logic to account for backspacing.
    if (isDeleting) {
      setUserInput(value)
    } else if (!isAddingSpecialChar) {
      const formattedValue = formatPhoneNumber(value)
      setUserInput(formattedValue)
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (!e.clipboardData) return
    const pastedText = e.clipboardData.getData('text/plain')
    const formattedValue = formatPhoneNumber(pastedText)
    setUserInput(formattedValue)
    e.preventDefault()
  }

  const handleModalSubmit = () => {
    const cleanedNumber = formatPhoneNumberForBlocking(userInput)
    onBlockNumber(cleanedNumber)
    onClose()
    setUserInput('')
  }

  return (
    <MangoModal
      isOpen={isOpen}
      onClose={() => {
        onClose()
        setUserInput('')
      }}
      header={t('blockedNumberModal.header')}
      size={Size.md}
      footer={
        <Flex css={styles.footer}>
          <MangoButton
            variant={MangoButtonVariant.Default_100}
            onClick={() => setUserInput('')}
          >
            {t('blockedNumberModal.clearButton')}
          </MangoButton>
          <MangoButton
            variant={MangoButtonVariant.Destructive_300}
            onClick={handleModalSubmit}
            isLoading={isLoading}
            disabled={!canBlockNumber}
          >
            {t('blockedNumberModal.blockButton')}
          </MangoButton>
        </Flex>
      }
    >
      <Flex css={styles.body}>
        <Text>{t('blockedNumberModal.description')}</Text>
        <Flex css={styles.inputContainer}>
          <Input
            value={userInput}
            onChange={handleInputChange}
            aria-label={t('blockedNumberModal.inputLabel')}
            placeholder={t('blockedNumberModal.inputPlaceholder')}
            type="tel"
            maxLength={14}
            onPaste={handlePaste}
          />
        </Flex>
      </Flex>
    </MangoModal>
  )
}
